import { useState } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { UseMutationResult } from '@tanstack/react-query';

import { EAiRunFeedbackSentiment, type TAiRunFeedbackInput, type TAiRunFeedbackMutationArgs } from '../types';
import { useBottomBarStyles } from '../hooks/useBottomBarStyles.hook';
import AiRunFeedbackNegativeDialog from './AiRunFeedbackNegativeDialog';
import AiRunFeedbackRatingButton from './AiRunFeedbackRatingButton';

const useStyles = makeStyles({
  root: {
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXXS,
  },
});

type TProps = {
  runId: string;
  feedbackMutation: UseMutationResult<void, Error, TAiRunFeedbackMutationArgs>;
  // Owned by the caller, living beside the run it rates, so it survives this view unmounting.
  submittedFeedback: TAiRunFeedbackInput | null;
  onFeedbackSubmitted: (feedback: TAiRunFeedbackInput) => void;
};

const AiRunFeedback = ({ runId, feedbackMutation, submittedFeedback, onFeedbackSubmitted }: TProps) => {
  const [isNegativeDialogOpen, setIsNegativeDialogOpen] = useState(false);

  const submitFeedback = (feedback: TAiRunFeedbackInput) => {
    feedbackMutation.mutate(
      { runId, feedback },
      {
        onSuccess: () => {
          onFeedbackSubmitted(feedback);
          setIsNegativeDialogOpen(false);
        },
      }
    );
  };

  const submitPositiveFeedback = () => {
    if (submittedFeedback?.sentiment === EAiRunFeedbackSentiment.POSITIVE) return;

    submitFeedback({ sentiment: EAiRunFeedbackSentiment.POSITIVE });
  };

  const bottomBarClasses = useBottomBarStyles();
  const classes = useStyles();

  return (
    <>
      <div className={mergeClasses(bottomBarClasses.bar, classes.root)}>
        <AiRunFeedbackRatingButton
          sentiment={EAiRunFeedbackSentiment.POSITIVE}
          isSelected={!isNegativeDialogOpen && submittedFeedback?.sentiment === EAiRunFeedbackSentiment.POSITIVE}
          disabled={feedbackMutation.isPending}
          onClick={submitPositiveFeedback}
        />
        <AiRunFeedbackRatingButton
          sentiment={EAiRunFeedbackSentiment.NEGATIVE}
          isSelected={isNegativeDialogOpen || submittedFeedback?.sentiment === EAiRunFeedbackSentiment.NEGATIVE}
          disabled={feedbackMutation.isPending}
          onClick={() => setIsNegativeDialogOpen(true)}
        />
      </div>

      <AiRunFeedbackNegativeDialog
        isOpen={isNegativeDialogOpen}
        setIsOpen={setIsNegativeDialogOpen}
        submittedFeedback={submittedFeedback}
        isSubmitting={feedbackMutation.isPending}
        onSubmit={submitFeedback}
      />
    </>
  );
};

export default AiRunFeedback;
